<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientId: number;
  export let organisationId: string | undefined;

  let versions: string[] = [];
  let clientVersion: string | null = null;
  let serverVersion: string | null = null;
  let selectedVersion = '';
  let clientExists = true;

  let isLoading = false;
  let isSaving = false;

  function handleApiError(error: unknown, message: string) {
    if (error instanceof Error) {
      console.error(`${message}:`, error.message);
      showToast({ title: 'Error', message: error.message, type: 'error' });
    } else {
      console.error(`${message}:`, error);
      showToast({ title: 'Error', message, type: 'error' });
    }
  }

  $: canSave = !!selectedVersion && selectedVersion !== serverVersion && !isSaving && clientExists;

  onMount(async () => {
    await loadData();
  });

  async function loadData() {
    isLoading = true;
    try {
      const [versionsResponse, clientVersionResponse] = await Promise.all([
        fetchWithAuth('/api/synergetic/connector-versions'),
        fetchWithAuth(`/api/synergetic/clients/${clientId}/connector-version`),
      ]);

      if (versionsResponse.ok) {
        const result = await versionsResponse.json();
        if (result.success) versions = result.data ?? [];
      } else {
        handleApiError(null, 'Failed to load connector version catalogue');
      }

      if (clientVersionResponse.ok) {
        const result = await clientVersionResponse.json();
        if (result.success) {
          clientExists = result.data.exists;
          clientVersion = result.data.clientVersion;
          serverVersion = result.data.serverVersion;
          selectedVersion = serverVersion ?? '';
        }
      } else {
        handleApiError(null, 'Failed to load client connector version');
      }
    } catch (error) {
      handleApiError(error, 'Failed to load Synergetics connector configuration');
    } finally {
      isLoading = false;
    }
  }

  async function saveServerVersion() {
    if (!selectedVersion || isSaving) return;

    isSaving = true;
    try {
      const response = await fetchWithAuth(
        `/api/synergetic/clients/${clientId}/server-connector-version/${encodeURIComponent(selectedVersion)}`,
        { method: 'PUT' },
      );

      if (response.ok) {
        serverVersion = selectedVersion;
        showToast({ title: 'Success', message: 'Server connector version updated', type: 'success' });
      } else if (response.status === 404) {
        handleApiError(null, `Client ${clientId} does not exist`);
      } else if (response.status === 400) {
        handleApiError(null, `Version ${selectedVersion} is not a valid connector version`);
      } else {
        const result = await response.json().catch(() => null);
        handleApiError(null, result?.error ?? 'Failed to update server connector version');
      }
    } catch (error) {
      handleApiError(error, 'Failed to update server connector version');
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="rounded-lg border border-surface-200 bg-white p-6">
  <h3 class="mb-4 text-lg font-semibold">Synergetics configuration</h3>

  {#if isLoading}
    <p class="text-sm text-gray-600">Loading connector version details...</p>
  {:else if !clientExists}
    <p class="text-sm text-gray-600">
      No Synergetic connector details were found for this client.
    </p>
  {:else}
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <span class="mb-2 block text-sm font-medium text-gray-700">Client connector version</span>
        <div class="rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
          {clientVersion ?? 'Unknown'}
        </div>
      </div>

      <div>
        <label for="syn-server-version" class="mb-2 block text-sm font-medium text-gray-700">
          Server connector version
        </label>
        <select
          id="syn-server-version"
          bind:value={selectedVersion}
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isSaving}
        >
          {#if !versions.includes(serverVersion ?? '')}
            <option value={serverVersion ?? ''}>{serverVersion ?? 'Unknown'}</option>
          {/if}
          {#each versions as version}
            <option value={version}>{version}</option>
          {/each}
        </select>
      </div>
    </div>

    <div class="mt-6 flex justify-end">
      <Button
        label={isSaving ? 'Saving...' : 'Save server version'}
        onClick={saveServerVersion}
        disabled={!canSave}
        colour="primary"
      />
    </div>
  {/if}
</div>
